import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, Lock, Eye, FileText, AlertCircle } from 'lucide-react';

const PrivacyNotice = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4" data-testid="privacy-notice-page">
      <div className="max-w-4xl mx-auto pt-4 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Shield className="h-8 w-8 mr-3 text-indigo-600" />
            Privacy & Confidentiality
          </h1>
          <p className="text-gray-600 mt-2">Your privacy and data security are our highest priorities</p>
        </div>

        {/* Privacy Commitment */}
        <Card className="mb-6 border-2 border-indigo-200 bg-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center text-indigo-900">
              <Lock className="h-6 w-6 mr-2" />
              Our Privacy Commitment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-indigo-800 leading-relaxed">
              <strong>Your data is handled confidentially.</strong> HealthLoop is designed with privacy at its core. 
              Your health information is encrypted, secure, and never shared without your explicit consent. 
              You have complete control over your data and can access, export, or delete it at any time.
            </p>
          </CardContent>
        </Card>

        {/* What Information We Collect */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-6 w-6 mr-2 text-gray-700" />
              What Information We Collect
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Preventive Health Intelligence Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Confidential wellness snapshots:</strong> Mood, energy levels, sleep quality, and symptoms you share</li>
                <li>Lifestyle patterns: Activity, hydration, rest, and medication adherence data</li>
                <li>Personalized preventive insights and early awareness alerts generated for your proactive care</li>
                <li>Health profile information you choose to provide (medical history, wellness goals)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Account Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Name and email address for account creation</li>
                <li>Profile information you choose to add</li>
                <li>Authentication credentials (securely encrypted)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* How We Protect Your Data */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-6 w-6 mr-2 text-green-700" />
              How We Protect Your Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Lock className="h-5 w-5 text-green-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Encryption</h4>
                  <p className="text-gray-700 text-sm">All data is encrypted in transit and at rest using industry-standard encryption protocols.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Eye className="h-5 w-5 text-blue-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Access Control</h4>
                  <p className="text-gray-700 text-sm">Only you can access your health data. We use secure authentication and never share your information without consent.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="h-5 w-5 text-purple-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Secure Infrastructure</h4>
                  <p className="text-gray-700 text-sm">We use secure, monitored servers with regular security audits and updates.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Your Privacy Rights</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span><strong>Access:</strong> You can view all your data at any time through your profile and history.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span><strong>Control:</strong> You decide what information to share and when.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span><strong>Deletion:</strong> You can delete your account and all associated data at any time.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span><strong>Portability:</strong> Export your health data whenever you need it.</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Confidentiality in Consultations */}
        <Card className="mb-6 border-2 border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="text-purple-900">Specialist Consultation Confidentiality</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-purple-800 leading-relaxed">
              All specialist consultation requests are handled with the highest level of confidentiality. 
              Your concerns and contact information are shared only with qualified professionals who are 
              bound by strict confidentiality agreements. Your consultation history is encrypted and 
              accessible only to you and your designated healthcare providers.
            </p>
          </CardContent>
        </Card>

        {/* Medical Disclaimer */}
        <Card className="border-2 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center text-orange-900">
              <AlertCircle className="h-6 w-6 mr-2" />
              Important Medical Disclaimer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-orange-900">
              <p className="font-semibold">HealthLoop supports preventive wellness and early awareness.</p>
              
              <p>HealthLoop provides:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Preventive health education</li>
                <li>Habit tracking and wellness monitoring</li>
                <li>Wellness insights and pattern recognition</li>
                <li>Supportive recommendations for healthy living</li>
              </ul>

              <p className="font-semibold mt-4">HealthLoop does NOT:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Diagnose medical conditions</li>
                <li>Treat diseases or medical conditions</li>
                <li>Replace licensed medical care or professional medical advice</li>
                <li>Provide emergency medical services</li>
              </ul>

              <p className="mt-4 font-semibold">
                Always consult with qualified healthcare providers for medical advice, diagnosis, or treatment. 
                In case of a medical emergency, call emergency services immediately.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact for Privacy Questions — routes through the same
            verified mailbox as support; we don't run a separate privacy@
            inbox so all communications land at support@healthloop.app. */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>
            Questions about your privacy? Contact us at{' '}
            <a
              href="mailto:support@healthloop.app?subject=Privacy%20question"
              className="text-emerald-700 hover:underline"
              data-testid="privacy-contact-email"
            >
              support@healthloop.app
            </a>
          </p>
          <p className="mt-2">Last updated: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyNotice;
